'use client'

import { forkAgent } from '@/lib/api'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { PAGE_PATH } from '@/lib/page-paths'
import { useRouter } from 'next/navigation'
import posthog from 'posthog-js'
import React, { useEffect, useRef, useState } from 'react'
import { ApiAgentForkResponse } from 'sprites-types/api/agents'

interface TemplateRedirectOverlayProps {
  agentId: string
}

const TemplateRedirectOverlay: React.FC<TemplateRedirectOverlayProps> = ({ agentId }) => {
  const { data: userData, isLoading: userLoading } = useGetUserQuery()
  const workspace = userData?.workspace
  const user = userData?.user
  const state = userLoading ? 'loading' : 'authenticated'
  const router = useRouter()
  const [error, setError] = useState(false)
  const redirectAttemptedRef = useRef(false)

  const redirectToTemplate = async (workspaceId: string) => {
    // Prevent multiple redirect attempts
    if (redirectAttemptedRef.current) {
      return
    }
    redirectAttemptedRef.current = true

    try {
      console.log('redirectToTemplate: forking agent from agent id', agentId)

      const response: ApiAgentForkResponse = await forkAgent(workspaceId, agentId)

      // Clear the URL parameters in case the user navigates back
      // We don't want to show the template redirect overlay again
      const currentPath = window.location.pathname
      const newUrl = `${currentPath}`
      window.history.replaceState({}, document.title, newUrl)

      const doProfiling = false
      const url = `${PAGE_PATH.AGENTS}/${response.agent.id}${doProfiling ? '?profiling=true' : ''}`

      router.push(url)
      console.log('redirectToTemplate: pushing to', url)

      posthog.capture('template.fork_success', {
        user_id: user?.id,
        agent_id: response.agent.id,
        workspace_id: workspaceId,
        fork_agent_id: agentId,
        agent_url: url,
      })
    } catch (err) {
      console.error(`redirectToTemplate: Error forking agent: ${agentId}`, err)
      setError(true)
    }
  }

  // Once user is available, redirect to the template
  useEffect(() => {
    if (user && state === 'authenticated' && !redirectAttemptedRef.current && !error) {
      if (!workspace?.id) {
        setError(true)
        console.error(`redirectToTemplate: Workspace not found ${workspace} user: ${user}`)
        return
      }

      redirectToTemplate(workspace.id)
    }
  }, [user, state, router, workspace, agentId])

  if (error) {
    return (
      <>
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"></div>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="flex w-full max-w-xs flex-col items-center rounded-lg bg-white p-8">
            <svg
              className="mb-4 h-12 w-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <p className="mb-2 text-lg font-medium text-gray-800">Something went wrong</p>
            <p className="mb-4 text-center text-gray-600">Please refresh the page to try again</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="flex w-full max-w-xs flex-col items-center rounded-lg bg-white p-8">
          <p className="mb-4 text-lg font-medium text-gray-800">Creating your app</p>
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600"></div>
        </div>
      </div>
    </>
  )
}

export default TemplateRedirectOverlay
